/**
 * ChatMessageUtils — Utility functions for message parsing.
 * - parseActionTags: extract ---ACTION: tags from message content
 * - extractArtifacts: detect renderable code blocks (html/jsx/tsx/svg)
 */

/* ── Action chip colors by action type ── */
export const ACTION_CHIP_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  OPEN_WINDOW:      { bg: 'rgba(99,102,241,0.15)',  text: '#818cf8', border: 'rgba(99,102,241,0.4)' },
  CLOSE_WINDOW:     { bg: 'rgba(239,68,68,0.12)',   text: '#f87171', border: 'rgba(239,68,68,0.35)' },
  CREATE_TASK:      { bg: 'rgba(16,185,129,0.12)',  text: '#34d399', border: 'rgba(16,185,129,0.35)' },
  RESEARCH:         { bg: 'rgba(245,158,11,0.12)',  text: '#fbbf24', border: 'rgba(245,158,11,0.35)' },
  RUN_SKILL:        { bg: 'rgba(236,72,153,0.12)',  text: '#f472b6', border: 'rgba(236,72,153,0.35)' },
  SOCIAL_POST:      { bg: 'rgba(59,130,246,0.12)',  text: '#60a5fa', border: 'rgba(59,130,246,0.35)' },
  BRAIN_LEARN:      { bg: 'rgba(139,92,246,0.12)',  text: '#a78bfa', border: 'rgba(139,92,246,0.35)' },
  CHANGE_WALLPAPER: { bg: 'rgba(20,184,166,0.12)',  text: '#2dd4bf', border: 'rgba(20,184,166,0.35)' },
  CHANGE_THEME:     { bg: 'rgba(251,191,36,0.12)',  text: '#fcd34d', border: 'rgba(251,191,36,0.35)' },
  OPEN_TERMINAL:    { bg: 'rgba(34,197,94,0.12)',   text: '#4ade80', border: 'rgba(34,197,94,0.35)' },
  DEFAULT:          { bg: 'rgba(148,163,184,0.12)', text: '#94a3b8', border: 'rgba(148,163,184,0.35)' },
}

export interface ParsedAction {
  type: string
  params: string[]
}

export interface CodeArtifact {
  lang: string
  code: string
}

/** Strip ---ACTION: tags from content and return them as structured data */
export function parseActionTags(content: string): { text: string; actions: ParsedAction[] } {
  const actionRegex = /---ACTION:\s*([A-Z_]+)(?:\s*\|\s*([^\n]*))?/g
  const actions: ParsedAction[] = []
  let match: RegExpExecArray | null

  while ((match = actionRegex.exec(content)) !== null) {
    const type = match[1].trim()
    const paramStr = match[2] ?? ''
    const params = paramStr.split('|').map(p => p.trim()).filter(Boolean)
    actions.push({ type, params })
  }

  const text = content.replace(/---ACTION:\s*[A-Z_]+(?:\s*\|[^\n]*)?\n?/g, '').trim()
  return { text, actions }
}

/** Extract renderable code artifacts (html/jsx/tsx/svg) from message content */
export function extractArtifacts(content: string): { text: string; artifacts: CodeArtifact[] } {
  const artifacts: CodeArtifact[] = []
  const artifactLangs = new Set(['html', 'jsx', 'tsx', 'svg'])

  const codeBlockRegex = /```([\w+-]*)\n([\s\S]*?)```/g
  let match: RegExpExecArray | null
  let text = content

  while ((match = codeBlockRegex.exec(content)) !== null) {
    const lang = match[1].toLowerCase().trim()
    const code = match[2]
    if (artifactLangs.has(lang)) {
      artifacts.push({ lang, code })
      text = text.replace(match[0], `[${lang.toUpperCase()} artifact rendered below]`)
    }
  }

  return { text, artifacts }
}
